import React, { useMemo } from 'react';
import './PricingSection.css';
import { DEFAULT_TAX_RATE, linkPricing, originalPriceWarning } from './pricingLinks';

const CURRENCIES = [
    { value: 'INR', label: 'Indian Rupee (₹)' },
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'AED', label: 'UAE Dirham (د.إ)' },
    { value: 'GBP', label: 'British Pound (£)' }
];

const PRICE_UNITS = [
    { value: 'per_night', label: 'Per Night' },
    { value: 'per_person', label: 'Per Person' },
    // values = the backend's price units (per_room isn't one — it's per_unit)
    { value: 'per_unit', label: 'Per Room / Tent' },
    { value: 'per_bed', label: 'Per Bed' },
    { value: 'per_package', label: 'Per Package' }
];

const PRICING_RULE_TYPES = [
    {
        value: 'weekend',
        label: 'Weekend',
        description: 'Apply a different price on weekends'
    },
    {
        value: 'weekday',
        label: 'Weekday',
        description: 'Apply a different price on weekdays'
    },
    {
        value: 'season',
        label: 'Season',
        description: 'Apply pricing for a specific season'
    },
    {
        value: 'date_range',
        label: 'Date Range',
        description: 'Apply pricing for selected dates'
    },
    {
        value: 'occupancy',
        label: 'Occupancy',
        description: 'Adjust pricing based on guests'
    },
    {
        value: 'holiday',
        label: 'Holiday',
        description: 'Apply pricing for holidays'
    },
    {
        value: 'festival',
        label: 'Festival',
        description: 'Apply pricing for festivals'
    },
    {
        value: 'last_minute',
        label: 'Last Minute',
        description: 'Pricing close to the stay date'
    },
    {
        value: 'early_bird',
        label: 'Early Bird',
        description: 'Pricing for early bookings'
    }
];

/*
 * Property-wide rules were never used when pricing a booking — special prices
 * are now set per unit (Units step → unit → Pricing tab → Special prices).
 * Kept (hidden) so older saved rules aren't lost.
 */
const SHOW_PROPERTY_RULES = false;

const emptyRule = () => ({
    name: '',
    type: 'weekend',
    startDate: '',
    endDate: '',
    days: [],
    price: 0,
    priceAdjustmentType: 'fixed',
    priceAdjustment: 0,
    minGuests: 1,
    maxGuests: 0,
    active: true
});

const toNumber = (value) => {
    if (value === '' || value === null || value === undefined) {
        return 0;
    }

    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
};

const getCurrencySymbol = (currency = 'INR') => {
    const symbols = {
        INR: '₹',
        USD: '$',
        EUR: '€',
        AED: 'د.إ'
    };

    return symbols[currency] || currency;
};

/*
 * NOTE ON DATA SHAPE:
 *
 * The backend stores pricing PER UNIT
 * (formData.units[i].pricing), not as a single
 * top-level formData.price. This "Base Pricing"
 * card is a convenience that writes the same
 * price to every unit at once. If you later edit
 * an individual unit's price from the Units step,
 * that per-unit edit will stick - this card only
 * fills in units that don't already have a price
 * set, plus lets you push a value to every unit
 * explicitly.
 *
 * Dynamic pricing rules are stored at
 * formData.seasonalPricing (top-level, shared
 * across all units) and are sent to the backend
 * close to as-is, so the field names here
 * (priceAdjustmentType/priceAdjustment) are mapped
 * to the backend's (adjustmentType/adjustmentValue)
 * at submit time in accommodationPayload.js.
 */

const PricingSection = ({ formData, updateField, errors = {}, canEdit = true }) => {
    const units = Array.isArray(formData?.units) ? formData.units : [];

    const firstUnit = units[0] || {};

    const pricing = firstUnit.pricing || {};

    const pricingRules = Array.isArray(formData?.seasonalPricing) ? formData.seasonalPricing : [];

    const currency = pricing.currency || 'INR';

    const symbol = getCurrencySymbol(currency);

    const actual = toNumber(pricing.actual);
    const tax = toNumber(pricing.tax);
    const reserveAmount = toNumber(pricing.reserveAmount);

    const calculatedTotal = actual + tax;

    const updatePricing = (field, value) => {
        if (!canEdit || units.length === 0) return;

        const updatedUnits = units.map((unit) => ({
            ...unit,
            pricing: {
                ...(unit.pricing || {}),
                [field]: value
            }
        }));

        updateField('units', updatedUnits);
    };

    const updateNumericPricing = (field, value) => {
        updatePricing(field, toNumber(value));
    };

    // base price / tax / original price / discount fill each other in (pricingLinks.js)
    const LINKED = ['actual', 'tax', 'total', 'higherPrice', 'discountPercent'];
    const updateLinkedPricing = (field, value) => {
        if (!canEdit || units.length === 0) return;
        const next = linkPricing(pricing, field, value);
        const changed = {};
        LINKED.forEach((key) => {
            if (next[key] !== undefined && next[key] !== pricing[key]) changed[key] = next[key];
        });
        updateField(
            'units',
            units.map((unit) => ({
                ...unit,
                pricing: { ...(unit.pricing || {}), ...changed }
            }))
        );
    };
    const originalWarning = originalPriceWarning(pricing);

    const updateRule = (index, field, value) => {
        if (!canEdit) return;

        const updatedRules = pricingRules.map((rule, ruleIndex) =>
            ruleIndex === index
                ? {
                      ...rule,
                      [field]: value
                  }
                : rule
        );

        updateField('seasonalPricing', updatedRules);
    };

    const addRule = (type = 'weekend') => {
        if (!canEdit) return;

        updateField('seasonalPricing', [
            ...pricingRules,
            {
                ...emptyRule(),
                type
            }
        ]);
    };

    const removeRule = (index) => {
        if (!canEdit) return;

        updateField(
            'seasonalPricing',
            pricingRules.filter((_, ruleIndex) => ruleIndex !== index)
        );
    };

    const toggleRuleDay = (index, day) => {
        if (!canEdit) return;

        const rule = pricingRules[index];

        const days = Array.isArray(rule.days) ? rule.days : [];

        const updatedDays = days.includes(day) ? days.filter((item) => item !== day) : [...days, day];

        updateRule(index, 'days', updatedDays);
    };

    const pricingSummary = useMemo(() => {
        return {
            base: actual,
            tax,
            total: calculatedTotal,
            reserve: reserveAmount
        };
    }, [actual, tax, calculatedTotal, reserveAmount]);

    return (
        <div className='pricing-section'>
            {/* =====================================================
          HEADER
      ====================================================== */}

            <div className='pricing-header'>
                <div>
                    <span className='section-eyebrow'>PRICING & REVENUE</span>

                    <h2>Pricing</h2>

                    <p>Configure your base price, taxes, discounts and dynamic pricing rules for this property.</p>
                </div>

                <div className='pricing-header-badge'>
                    <span>Base price</span>

                    <strong>
                        {symbol}
                        {actual.toLocaleString('en-IN')}
                    </strong>

                    <small>{PRICE_UNITS.find((item) => item.value === pricing.unit)?.label || 'Per Night'}</small>
                </div>
            </div>

            {errors.pricing && (
                <div className='form-error-summary'>
                    <strong>Please fix the following</strong>
                    <p>{errors.pricing}</p>
                </div>
            )}

            {units.length === 0 && (
                <div className='form-error-summary'>
                    <strong>No units added yet</strong>
                    <p>Add at least one accommodation unit in the Units step before setting pricing.</p>
                </div>
            )}

            {/* =====================================================
          BASIC PRICE
      ====================================================== */}

            <div className='pricing-card'>
                <div className='pricing-card-header'>
                    <div>
                        <h3>Base Pricing</h3>
                        <p>Set the standard price guests will see before applicable discounts or dynamic pricing.</p>
                    </div>
                </div>

                <div className='pricing-form-grid'>
                    {/* Price name */}
                    <div className='pricing-field full'>
                        <label>Pricing Name</label>

                        <input
                            type='text'
                            value={pricing.name || ''}
                            onChange={(event) => updatePricing('name', event.target.value)}
                            placeholder='e.g. Standard Tariff'
                            disabled={!canEdit}
                        />

                        <span className='field-hint'>Internal name used to identify this pricing plan.</span>
                    </div>

                    {/* Currency */}
                    <div className='pricing-field'>
                        <label>Currency</label>

                        <select
                            value={currency}
                            onChange={(event) => updatePricing('currency', event.target.value)}
                            disabled={!canEdit}
                        >
                            {CURRENCIES.map((item) => (
                                <option value={item.value} key={item.value}>
                                    {item.label}
                                </option>
                            ))}
                        </select>

                        {errors.currency && <span className='field-error'>{errors.currency}</span>}
                    </div>

                    {/* Unit */}
                    <div className='pricing-field'>
                        <label>Price Unit</label>

                        <select
                            value={pricing.unit || 'per_night'}
                            onChange={(event) => updatePricing('unit', event.target.value)}
                            disabled={!canEdit}
                        >
                            {PRICE_UNITS.map((item) => (
                                <option value={item.value} key={item.value}>
                                    {item.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Actual */}
                    <div className='pricing-field'>
                        <label>
                            Base Price
                            <span className='required'>*</span>
                        </label>

                        <div className='money-input'>
                            <span>{symbol}</span>

                            <input
                                type='number'
                                min='0'
                                value={pricing.actual === 0 ? '' : pricing.actual}
                                onChange={(event) => updateLinkedPricing('actual', event.target.value)}
                                placeholder='0'
                                disabled={!canEdit}
                            />
                        </div>

                        {errors.actual && <span className='field-error'>{errors.actual}</span>}
                    </div>

                    {/* Tax */}
                    <div className='pricing-field'>
                        <label>Tax</label>

                        <div className='money-input'>
                            <span>{symbol}</span>

                            <input
                                type='number'
                                min='0'
                                value={pricing.tax === 0 ? '' : pricing.tax}
                                onChange={(event) => updateLinkedPricing('tax', event.target.value)}
                                placeholder='0'
                                disabled={!canEdit}
                            />
                        </div>

                        <span className='field-hint'>
                            {DEFAULT_TAX_RATE}% of the base price, filled in automatically. You can change it.
                        </span>
                    </div>

                    {/* Higher price */}
                    <div className='pricing-field'>
                        <label>Higher / Original Price</label>

                        <div className='money-input'>
                            <span>{symbol}</span>

                            <input
                                type='number'
                                min='0'
                                value={pricing.higherPrice === 0 ? '' : pricing.higherPrice}
                                onChange={(event) => updateLinkedPricing('higherPrice', event.target.value)}
                                placeholder='0'
                                disabled={!canEdit}
                            />
                        </div>

                        {originalWarning ? (
                            <span className='field-error'>{originalWarning}</span>
                        ) : (
                            <span className='field-hint'>
                                Shown crossed out. Fills in from the discount, and the other way round.
                            </span>
                        )}
                    </div>

                    {/* Discount */}
                    <div className='pricing-field'>
                        <label>Discount Percentage</label>

                        <div className='suffix-input'>
                            <input
                                type='number'
                                min='0'
                                max='100'
                                value={pricing.discountPercent === 0 ? '' : pricing.discountPercent}
                                onChange={(event) => updateLinkedPricing('discountPercent', event.target.value)}
                                placeholder='0'
                                disabled={!canEdit}
                            />

                            <span>%</span>
                        </div>

                        <span className='field-hint'>Fills in from the original price, and the other way round.</span>
                    </div>

                    {/* Reserve */}
                    <div className='pricing-field'>
                        <label>Reserve Amount</label>

                        <div className='money-input'>
                            <span>{symbol}</span>

                            <input
                                type='number'
                                min='0'
                                value={pricing.reserveAmount === 0 ? '' : pricing.reserveAmount}
                                onChange={(event) => updateNumericPricing('reserveAmount', event.target.value)}
                                placeholder='0'
                                disabled={!canEdit}
                            />
                        </div>

                        <span className='field-hint'>Optional amount required to reserve the booking.</span>
                    </div>
                </div>

                {units.length > 1 && (
                    <div className='field-hint' style={{ marginTop: 8 }}>
                        This price applies to all {units.length} units. To give a specific unit its own price, edit that
                        unit's Pricing tab in the Units step afterwards.
                    </div>
                )}
            </div>

            {/* =====================================================
          PRICE SUMMARY
      ====================================================== */}

            <div className='price-summary-card'>
                <div className='price-summary-left'>
                    <span className='price-summary-label'>Guest Price Preview</span>

                    <div className='price-summary-main'>
                        <strong>
                            {symbol}
                            {pricingSummary.total.toLocaleString('en-IN')}
                        </strong>

                        <span>
                            {PRICE_UNITS.find(
                                (item) => item.value === (pricing.unit || 'per_night')
                            )?.label?.toLowerCase() || 'per night'}
                        </span>
                    </div>
                </div>

                <div className='price-summary-breakdown'>
                    <div>
                        <span>Base price</span>
                        <strong>
                            {symbol}
                            {pricingSummary.base.toLocaleString('en-IN')}
                        </strong>
                    </div>

                    <div>
                        <span>Tax</span>
                        <strong>
                            {symbol}
                            {pricingSummary.tax.toLocaleString('en-IN')}
                        </strong>
                    </div>

                    <div className='summary-total'>
                        <span>Total</span>
                        <strong>
                            {symbol}
                            {pricingSummary.total.toLocaleString('en-IN')}
                        </strong>
                    </div>
                </div>
            </div>

            {/* =====================================================
          DYNAMIC PRICING
      ====================================================== */}

            {SHOW_PROPERTY_RULES && (
                <div className='pricing-card'>
                    <div className='pricing-card-header pricing-rules-heading'>
                        <div>
                            <h3>Dynamic Pricing Rules</h3>

                            <p>Configure special prices for weekends, seasons, date ranges or guest occupancy.</p>
                        </div>

                        {canEdit && (
                            <button type='button' className='add-pricing-rule-btn' onClick={() => addRule()}>
                                + Add Rule
                            </button>
                        )}
                    </div>

                    {/* Rule templates */}
                    {canEdit && pricingRules.length === 0 && (
                        <div className='pricing-rule-templates'>
                            {PRICING_RULE_TYPES.map((type) => (
                                <button
                                    type='button'
                                    key={type.value}
                                    className='pricing-rule-template'
                                    onClick={() => addRule(type.value)}
                                >
                                    <span className='template-icon'>
                                        {type.value === 'weekend'
                                            ? '📅'
                                            : type.value === 'weekday'
                                              ? '🗓️'
                                              : type.value === 'season'
                                                ? '🌤️'
                                                : type.value === 'date_range'
                                                  ? '📆'
                                                  : '👥'}
                                    </span>

                                    <span>
                                        <strong>{type.label}</strong>
                                        <small>{type.description}</small>
                                    </span>

                                    <b>+</b>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Rules */}
                    {pricingRules.length > 0 && (
                        <div className='pricing-rules-list'>
                            {pricingRules.map((rule, index) => (
                                <PricingRule
                                    key={`pricing-rule-${index}`}
                                    rule={rule}
                                    index={index}
                                    canEdit={canEdit}
                                    symbol={symbol}
                                    updateRule={updateRule}
                                    removeRule={removeRule}
                                    toggleRuleDay={toggleRuleDay}
                                />
                            ))}
                        </div>
                    )}

                    {!canEdit && pricingRules.length === 0 && (
                        <div className='pricing-empty'>
                            <div className='pricing-empty-icon'>💰</div>

                            <strong>No dynamic pricing rules</strong>

                            <p>No additional pricing rules have been configured for this property.</p>
                        </div>
                    )}
                </div>
            )}

            {/* =====================================================
          INFORMATION
      ====================================================== */}

            <div className='pricing-info'>
                <div className='pricing-info-icon'>💡</div>

                <div>
                    <strong>Weekend, season & offer prices</strong>

                    <p>
                        The base price is the normal nightly rate. To charge a different price on weekends, in a season,
                        on holidays, or for early-bird / last-minute bookings, open the Units step, edit a unit and use{' '}
                        <b>Pricing → Special prices</b>. Guests are charged those prices automatically.
                    </p>
                </div>
            </div>
        </div>
    );
};

/* =========================================================
   PRICING RULE COMPONENT
========================================================= */

const PricingRule = ({ rule, index, canEdit, symbol, updateRule, removeRule, toggleRuleDay }) => {
    const type = rule.type || 'weekend';

    return (
        <div className='pricing-rule'>
            <div className='pricing-rule-top'>
                <div className='pricing-rule-title'>
                    <div className='pricing-rule-number'>{index + 1}</div>

                    <div>
                        <strong>
                            {rule.name ||
                                PRICING_RULE_TYPES.find((item) => item.value === type)?.label ||
                                'Pricing Rule'}
                        </strong>

                        <span>
                            {PRICING_RULE_TYPES.find((item) => item.value === type)?.description ||
                                'Custom pricing rule'}
                        </span>
                    </div>
                </div>

                <div className='pricing-rule-actions'>
                    <label className='rule-status'>
                        <input
                            type='checkbox'
                            checked={rule.active !== false}
                            onChange={(event) => updateRule(index, 'active', event.target.checked)}
                            disabled={!canEdit}
                        />

                        <span className='rule-status-switch' />

                        <span>{rule.active !== false ? 'Active' : 'Inactive'}</span>
                    </label>

                    {canEdit && (
                        <button type='button' className='delete-rule-btn' onClick={() => removeRule(index)}>
                            Delete
                        </button>
                    )}
                </div>
            </div>

            <div className='pricing-rule-body'>
                <div className='pricing-rule-grid'>
                    {/* Name */}
                    <div className='pricing-field'>
                        <label>Rule Name</label>

                        <input
                            type='text'
                            value={rule.name || ''}
                            onChange={(event) => updateRule(index, 'name', event.target.value)}
                            placeholder='e.g. Weekend Pricing'
                            disabled={!canEdit}
                        />
                    </div>

                    {/* Type */}
                    <div className='pricing-field'>
                        <label>Rule Type</label>

                        <select
                            value={type}
                            onChange={(event) => updateRule(index, 'type', event.target.value)}
                            disabled={!canEdit}
                        >
                            {PRICING_RULE_TYPES.map((item) => (
                                <option value={item.value} key={item.value}>
                                    {item.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Adjustment type */}
                    <div className='pricing-field'>
                        <label>Adjustment Type</label>

                        <select
                            value={rule.priceAdjustmentType || 'fixed'}
                            onChange={(event) => updateRule(index, 'priceAdjustmentType', event.target.value)}
                            disabled={!canEdit}
                        >
                            <option value='fixed'>Fixed Price</option>

                            <option value='percentage'>Percentage</option>
                        </select>
                    </div>

                    {/* Adjustment */}
                    <div className='pricing-field'>
                        <label>
                            Adjustment
                            {rule.priceAdjustmentType === 'percentage' ? ' (%)' : ''}
                        </label>

                        <div className='money-input'>
                            <span>{rule.priceAdjustmentType === 'percentage' ? '%' : symbol}</span>

                            <input
                                type='number'
                                min='0'
                                value={rule.priceAdjustment || ''}
                                onChange={(event) => updateRule(index, 'priceAdjustment', toNumber(event.target.value))}
                                placeholder='0'
                                disabled={!canEdit}
                            />
                        </div>
                    </div>

                    {/* Direct price */}
                    <div className='pricing-field'>
                        <label>Override Price</label>

                        <div className='money-input'>
                            <span>{symbol}</span>

                            <input
                                type='number'
                                min='0'
                                value={rule.price || ''}
                                onChange={(event) => updateRule(index, 'price', toNumber(event.target.value))}
                                placeholder='Optional'
                                disabled={!canEdit}
                            />
                        </div>

                        <span className='field-hint'>Use this if the rule should replace the base price.</span>
                    </div>

                    {/* Min guests */}
                    <div className='pricing-field'>
                        <label>Minimum Guests</label>

                        <input
                            type='number'
                            min='1'
                            value={rule.minGuests || 1}
                            onChange={(event) =>
                                updateRule(index, 'minGuests', Math.max(1, toNumber(event.target.value)))
                            }
                            disabled={!canEdit}
                        />
                    </div>

                    {/* Max guests */}
                    <div className='pricing-field'>
                        <label>Maximum Guests</label>

                        <input
                            type='number'
                            min='0'
                            value={rule.maxGuests || ''}
                            onChange={(event) => updateRule(index, 'maxGuests', toNumber(event.target.value))}
                            placeholder='No limit'
                            disabled={!canEdit}
                        />
                    </div>
                </div>

                {/* Weekend / Weekday */}
                {(type === 'weekend' || type === 'weekday') && (
                    <div className='rule-days-section'>
                        <label>Select Days</label>

                        <div className='rule-days'>
                            {[
                                ['mon', 'Mon'],
                                ['tue', 'Tue'],
                                ['wed', 'Wed'],
                                ['thu', 'Thu'],
                                ['fri', 'Fri'],
                                ['sat', 'Sat'],
                                ['sun', 'Sun']
                            ].map(([value, label]) => {
                                const selected = (rule.days || []).includes(value);

                                return (
                                    <button
                                        type='button'
                                        key={value}
                                        className={`rule-day ${selected ? 'selected' : ''}`}
                                        onClick={() => toggleRuleDay(index, value)}
                                        disabled={!canEdit}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>

                        <span className='field-hint'>
                            The backend applies this rule across its Start/End Date range; day selection is a display
                            hint only for now.
                        </span>
                    </div>
                )}

                {/* Season / Date Range / Weekend / Weekday / Occupancy all need a date range on the backend */}
                <div className='rule-date-section'>
                    <div className='pricing-field'>
                        <label>
                            Start Date
                            <span className='required'>*</span>
                        </label>

                        <input
                            type='date'
                            value={rule.startDate || ''}
                            onChange={(event) => updateRule(index, 'startDate', event.target.value)}
                            disabled={!canEdit}
                        />
                    </div>

                    <div className='pricing-field'>
                        <label>
                            End Date
                            <span className='required'>*</span>
                        </label>

                        <input
                            type='date'
                            value={rule.endDate || ''}
                            onChange={(event) => updateRule(index, 'endDate', event.target.value)}
                            disabled={!canEdit}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingSection;
